import {
    Alert,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
} from '@mui/material'
import { useEffect, useState } from 'react'
import config from '../../../config.json'
import { getAtomicTemplatesForCollectionSchema } from '../../helpers/Api'
import { sleep } from '../../helpers/FormatLinks'
import { getBoostAction } from '../../helpers/WaxApi'
import { DropAlert } from '../DropAlert'
import { DropPaper } from '../DropPaper'
import { useSharedState } from '../../waxplorer/Waxplorer'

export const TemplateRequirements = ({
    collectionName,
    schemaName,
    staticAttributes,
    setSelectedTemplateId,
}) => {
    const [templateCandidates, setTemplateCandidates] = useState(null)
    const [isCreating, setIsCreating] = useState(false)
    const [error, setError] = useState('')
    const [selectedEditTemplateId, setSelectedEditTemplateId] = useState(null)
    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const fetchTemplateId = async (transactionId) => {
        let actions = []
        let tries = 0
        while (actions.length === 0) {
            await sleep(tries * 1000)
            const url =
                (process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
                    ? config.hyperiontest
                    : config.hyperion) +
                `/v2/history/get_transaction?id=${transactionId}`
            const response = await fetch(url)
            const json = await response.json()
            if (json.actions) {
                actions = json.actions
            }
            tries++
        }
        for (const action of actions) {
            if (action.act.name === 'lognewtempl') {
                setSelectedTemplateId(action.act.data.template_id)
                break
            }
        }
    }

    const createTemplate = async () => {
        setIsCreating(true)
        const actions = [getBoostAction(activeUser)]

        let immutableData = []

        for (const attribute of staticAttributes) {
            const key = attribute.name
            const type = ['image', 'ipfs'].includes(attribute.type)
                ? 'string'
                : attribute.type
            const value = [type, attribute.value]
            immutableData.push({ key, value })
        }

        const data = {
            authorized_creator: userName,
            collection_name: collectionName,
            schema_name: schemaName,
            transferable: true,
            burnable: true,
            max_supply: 0,
            immutable_data: immutableData,
        }

        const action = {
            account: 'atomicassets',
            name: 'createtempl',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: data,
        }

        actions.push(action)

        let transferError = null
        let transactionId = null

        try {
            const result = await activeUser.session.transact(
                {
                    actions: actions,
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )
            transactionId = result?.response?.transaction_id
        } catch (e) {
            transferError = e.message
            setError(e.message)
            setIsCreating(false)
            return
        }

        await fetchTemplateId(transactionId)

        setIsCreating(false)
    }

    const parseTemplates = (templates) => {
        const tempTemplateCandidates = []
        let requiredImmutableData = {}
        for (const attribute of staticAttributes) {
            requiredImmutableData[attribute.name] = attribute.value
        }
        for (const template of templates.data) {
            if (
                JSON.stringify(requiredImmutableData) ===
                JSON.stringify(template.immutable_data)
            ) {
                tempTemplateCandidates.push(template.template_id)
            }
        }
        if (tempTemplateCandidates.length > 0) {
            setSelectedEditTemplateId(tempTemplateCandidates[0])
        }
        setTemplateCandidates(tempTemplateCandidates)
    }

    useEffect(() => {
        if (collectionName && schemaName) {
            getAtomicTemplatesForCollectionSchema(
                collectionName,
                schemaName,
            ).then(parseTemplates)
        }
    }, [collectionName, schemaName])

    return (
        <>
            {templateCandidates === null || isCreating ? (
                <div className="flex justify-center">
                    <CircularProgress />
                </div>
            ) : templateCandidates.length === 0 ? (
                <DropPaper>
                    We couldn't find a matching template that can be used to
                    mint the assets. You will need to create one.
                    <div className="flex justify-center my-5">
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                createTemplate()
                            }}
                        >
                            Create Template
                        </Button>
                    </div>
                    <DropAlert condition={error !== ''} severity="error">
                        {error}
                    </DropAlert>
                </DropPaper>
            ) : (
                <DropPaper>
                    <Alert severity="info">
                        You will need a template that can be used to mint the
                        assets. You can select a matching one from this schema,
                        or we can create one for you.
                    </Alert>
                    <DropAlert condition={error !== ''} severity="error">
                        {error}
                    </DropAlert>
                    <div className="my-5" />
                    <div className="flex flex-wrap justify-center">
                        <div>
                            <div className="flex flex-wrap justify-center">
                                <div className="w-54 mx-2 my-auto">
                                    <FormControl fullWidth={true} size="small">
                                        <InputLabel>
                                            Select Template...
                                        </InputLabel>
                                        <Select
                                            value={
                                                selectedEditTemplateId
                                                    ? selectedEditTemplateId
                                                    : ''
                                            }
                                            label="Select Template..."
                                            onChange={(e) => {
                                                setSelectedEditTemplateId(
                                                    e.target.value,
                                                )
                                            }}
                                        >
                                            {templateCandidates.map(
                                                (candidate) => (
                                                    <MenuItem value={candidate}>
                                                        {candidate}
                                                    </MenuItem>
                                                ),
                                            )}
                                        </Select>
                                    </FormControl>
                                </div>
                                <div className="w-54 mx-2 my-auto">
                                    <Button
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        disabled={
                                            selectedEditTemplateId === null
                                        }
                                        onClick={() =>
                                            setSelectedTemplateId(
                                                selectedEditTemplateId,
                                            )
                                        }
                                    >
                                        Continue with selection
                                    </Button>
                                </div>
                            </div>
                            <div className="font-bold text-center my-5">
                                - OR -
                            </div>
                            <div className="flex flex-wrap justify-center">
                                <div className="w-54 mx-2 my-auto">
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        onClick={(e) => createTemplate()}
                                    >
                                        Create Template
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </DropPaper>
            )}
        </>
    )
}
